import { useState } from "react";
import { Button, Card, CardBody, CardHeader, Field, TextInput, FailClosedNotice } from "@odingard/design-system";
import { useAuth } from "../../lib/auth";
import { proposalApi } from "../../lib/api";
import type { AskResult } from "@odingard/sdk-client";
import { ApiError } from "@odingard/sdk-client";
import { DemoModeBanner, GroundedEvidence, CitationList } from "../../components/ProposalGrounded";

// The Ask the Solicitation surface is strictly source-grounded: when the
// backend cannot ground an answer it returns grounded=false and we render a
// fail-closed notice instead of any generated text.
export function PaiAsk() {
  const { activeOrg } = useAuth();
  const orgId = activeOrg?.id ?? "";
  const [solicitationId, setSolicitationId] = useState("");
  const [question, setQuestion] = useState("");
  const [result, setResult] = useState<AskResult | null>(null);
  const [demo, setDemo] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  async function ask() {
    if (!question.trim()) return;
    setLoading(true);
    setError(null);
    setResult(null);
    try {
      const res = await proposalApi.askSolicitation(orgId, solicitationId.trim(), question.trim());
      setResult(res.data);
      setDemo(res.demoMode);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Request failed.");
    } finally {
      setLoading(false);
    }
  }

  const grounded = result?.grounded && result.answer;

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Ask the Solicitation</h1>
          <p>Grounded answers with citations, never ungrounded guesses.</p>
        </div>
      </div>

      <DemoModeBanner show={demo} />

      <Card>
        <CardHeader title="Ask a question" />
        <CardBody>
          <div className="ods-stack">
            <Field label="Solicitation ID">
              {(id) => <TextInput id={id} value={solicitationId} onChange={(e) => setSolicitationId(e.target.value)} />}
            </Field>
            <Field label="Question">
              {(id) => (
                <TextInput
                  id={id}
                  value={question}
                  onChange={(e) => setQuestion(e.target.value)}
                  placeholder="e.g. What is the technical volume page limit?"
                />
              )}
            </Field>
            <div>
              <Button size="sm" onClick={ask} disabled={loading || !question.trim()}>
                {loading ? "Asking…" : "Ask"}
              </Button>
            </div>
          </div>
        </CardBody>
      </Card>

      {error && <FailClosedNotice message={error} />}
      {result && !grounded && (
        <FailClosedNotice message="No grounded answer is available for this question from the solicitation. Try rephrasing or upload the relevant document." />
      )}
      {result && grounded && (
        <Card>
          <CardHeader title="Answer" />
          <CardBody>
            <div className="ods-stack">
              <p style={{ margin: 0 }}>{result.answer}</p>
              <GroundedEvidence meta={result} />
              <CitationList citations={result.citations ?? []} />
            </div>
          </CardBody>
        </Card>
      )}
    </>
  );
}
